import { useCallback, useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { SwatchKind } from "@/components/primitives";
import { moderateComment } from "@/lib/commentModeration";

// useComments — load + manage comments (with replies, likes, and pinning)
// for a single scan_id. Joins author profile snapshots, likes, and
// scan-owner identity so the UI can render usernames, like counts, and
// owner-only pin controls without a second round-trip.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface Comment {
  id: string;
  scan_id: string;
  user_id: string;
  body: string;
  created_at: string;
  parent_id: string | null;
  pinned: boolean;
  like_count: number;
  liked_by_me: boolean;
  handle: string;
  display_name: string | null;
  avatar: SwatchKind | string;
}

export function useComments(scanId: string | null, opts: { autoLoad?: boolean } = {}) {
  const { autoLoad = true } = opts;
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [scanOwnerId, setScanOwnerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  const isReal = !!scanId && UUID_RE.test(scanId);

  const load = useCallback(async () => {
    if (!scanId || !isReal) {
      setComments([]);
      setScanOwnerId(null);
      return;
    }
    setLoading(true);

    // Pull scan owner so the UI can show pin controls only to them.
    const { data: scanRow } = await supabase
      .from("scans")
      .select("user_id")
      .eq("id", scanId)
      .maybeSingle();
    setScanOwnerId(scanRow?.user_id ?? null);

    const { data: rows, error } = await supabase
      .from("post_comments")
      .select("id, scan_id, user_id, body, created_at, parent_id, pinned")
      .eq("scan_id", scanId)
      .order("created_at", { ascending: true })
      .limit(200);
    if (error || !rows) {
      setLoading(false);
      return;
    }

    const userIds = Array.from(new Set(rows.map((r) => r.user_id)));
    const profilesRes = userIds.length
      ? await (supabase as any)
          .from("profiles_public")
          .select("id, handle, display_name, avatar")
          .in("id", userIds)
      : { data: [] as Array<{ id: string; handle: string; display_name: string | null; avatar: string }> };
    const byId = new Map(
      ((profilesRes.data ?? []) as Array<{ id: string; handle: string; display_name: string | null; avatar: string }>)
        .map((p) => [p.id, p] as const)
    );

    // Likes — fan out one IN-query for every comment on this scan.
    const commentIds = rows.map((r) => r.id);
    const likesRes = commentIds.length
      ? await supabase.from("comment_likes").select("comment_id, user_id").in("comment_id", commentIds)
      : { data: [] as Array<{ comment_id: string; user_id: string }> };
    const likeCounts = new Map<string, number>();
    const likedByMe = new Set<string>();
    for (const l of likesRes.data ?? []) {
      likeCounts.set(l.comment_id, (likeCounts.get(l.comment_id) ?? 0) + 1);
      if (user && l.user_id === user.id) likedByMe.add(l.comment_id);
    }

    setComments(
      rows.map((r) => {
        const p = byId.get(r.user_id);
        return {
          ...r,
          like_count: likeCounts.get(r.id) ?? 0,
          liked_by_me: likedByMe.has(r.id),
          handle: p?.handle ?? "anon",
          display_name: p?.display_name ?? null,
          avatar: (p?.avatar as SwatchKind | string) ?? "olive",
        };
      }),
    );
    setLoading(false);
  }, [scanId, isReal, user]);

  useEffect(() => {
    if (autoLoad) void load();
  }, [autoLoad, load]);

  const add = useCallback(
    async (body: string, parentId: string | null = null): Promise<boolean> => {
      if (!user) {
        toast.error("Sign in to leave a note");
        return false;
      }
      if (!scanId || !isReal) {
        toast.info("Comments aren't enabled on demo posts");
        return false;
      }
      const trimmed = body.trim().slice(0, 500);
      if (!trimmed) return false;
      const verdict = moderateComment(trimmed);
      if (!verdict.ok) {
        toast.error(verdict.reason ?? "This note can't be posted.");
        return false;
      }
      setPosting(true);
      const { data, error } = await supabase
        .from("post_comments")
        .insert({ scan_id: scanId, user_id: user.id, body: trimmed, parent_id: parentId })
        .select("id, scan_id, user_id, body, created_at, parent_id, pinned")
        .single();
      setPosting(false);
      if (error || !data) {
        toast.error("Couldn't post your note");
        return false;
      }
      const { data: p } = await supabase
        .from("profiles")
        .select("handle, display_name, avatar")
        .eq("id", user.id)
        .maybeSingle();
      setComments((prev) => [
        ...prev,
        {
          ...data,
          like_count: 0,
          liked_by_me: false,
          handle: p?.handle ?? "you",
          display_name: p?.display_name ?? null,
          avatar: (p?.avatar as SwatchKind | string) ?? "olive",
        },
      ]);
      return true;
    },
    [user, scanId, isReal],
  );

  const remove = useCallback(
    async (id: string) => {
      const before = comments;
      setComments((c) => c.filter((x) => x.id !== id && x.parent_id !== id));
      const { error } = await supabase.from("post_comments").delete().eq("id", id);
      if (error) {
        setComments(before);
        toast.error("Couldn't delete that note");
      }
    },
    [comments],
  );

  // Optimistic like toggle.
  const toggleLike = useCallback(
    async (id: string) => {
      if (!user) { toast.error("Sign in to like notes"); return; }
      const target = comments.find((c) => c.id === id);
      if (!target) return;
      const wasLiked = target.liked_by_me;
      setComments((cur) => cur.map((c) => c.id === id
        ? { ...c, liked_by_me: !wasLiked, like_count: c.like_count + (wasLiked ? -1 : 1) }
        : c));
      const { error } = wasLiked
        ? await supabase.from("comment_likes").delete().eq("comment_id", id).eq("user_id", user.id)
        : await supabase.from("comment_likes").insert({ comment_id: id, user_id: user.id });
      if (error) {
        setComments((cur) => cur.map((c) => c.id === id
          ? { ...c, liked_by_me: wasLiked, like_count: c.like_count + (wasLiked ? 1 : -1) }
          : c));
        toast.error("Couldn't update like");
      }
    },
    [user, comments],
  );

  // Pin/unpin a comment. RLS only allows the scan owner to update the pinned
  // flag. Multiple comments can be pinned simultaneously — toggling one
  // doesn't affect the others. Clicking pin again on a pinned comment unpins it.
  const togglePin = useCallback(
    async (id: string) => {
      if (!user || user.id !== scanOwnerId) return;
      const target = comments.find((c) => c.id === id);
      if (!target) return;
      const wasPinned = target.pinned;
      setComments((cur) => cur.map((c) =>
        c.id === id ? { ...c, pinned: !wasPinned } : c
      ));
      const { error } = await supabase.from("post_comments").update({ pinned: !wasPinned }).eq("id", id);
      if (error) {
        toast.error("Couldn't update pin");
        void load();
      }
    },
    [user, scanOwnerId, comments, load],
  );

  return { comments, loading, posting, add, remove, toggleLike, togglePin, scanOwnerId, refresh: load };
}

// Lightweight count-only loader for the Feed list (avoids fetching full bodies).
export function useCommentCounts(scanIds: string[]) {
  const [counts, setCounts] = useState<Record<string, number>>({});

  const realIds = scanIds.filter((id) => UUID_RE.test(id));
  const stableKey = realIds.slice().sort().join(",");

  useEffect(() => {
    if (realIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("post_comments")
        .select("scan_id")
        .in("scan_id", realIds);
      if (cancelled || error) return;
      const next: Record<string, number> = {};
      for (const row of data ?? []) {
        next[row.scan_id] = (next[row.scan_id] ?? 0) + 1;
      }
      setCounts(next);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableKey]);

  const refreshSingle = useCallback(async (scanId: string) => {
    if (!UUID_RE.test(scanId)) return;
    const { data } = await supabase
      .from("post_comments")
      .select("id")
      .eq("scan_id", scanId);
    setCounts((c) => ({ ...c, [scanId]: (data ?? []).length }));
  }, []);

  return {
    countFor: (scanId: string) => counts[scanId] ?? 0,
    bump: (scanId: string, by = 1) =>
      setCounts((c) => ({ ...c, [scanId]: Math.max(0, (c[scanId] ?? 0) + by) })),
    refreshSingle,
  };
}
